import { useRef, useState, useEffect, useCallback } from "react";
import "./AuriaVisualizer.css";

/* ============================================================
   AURIA Visualizer — Guide de respiration (prototype vidéo)
   - Le cycle de respiration pilote AURIA (échelle continue, sans à-coup)
   - Dolly zoom : le fond contre-zoome par rapport à AURIA (effet Vertigo)
   - Fond depuis public/backgrounds/, sélectionnable
   - La musique colore seulement la lueur
   ============================================================ */

const clampNum = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
function smoothstep(t) {
  const p = clampNum(t, 0, 1);
  return p * p * (3 - 2 * p);
}

/* Bornes d'échelle de respiration */
const SCALE_MIN = 0.8;
const SCALE_MAX = 1.34;
const RING_C = 628.32; // 2 * PI * 100

/* Forme ronde organique (reprise de AIComposerCard). */
function makeBlobPath({ t, cx = 120, cy = 120, radius = 56, wobble = 1.6, points = 28, phase = 0 }) {
  const pts = [];
  for (let i = 0; i < points; i += 1) {
    const a = (Math.PI * 2 * i) / points;
    const n =
      Math.sin(t * 0.8 + i * 0.8 + phase) * wobble +
      Math.sin(t * 0.42 + i * 1.6 + phase) * wobble * 0.5 +
      Math.cos(t * 0.57 + i * 2.2 + phase) * wobble * 0.32;
    const r = radius + n;
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
  for (let i = 0; i < points; i += 1) {
    const p0 = pts[(i - 1 + points) % points];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % points];
    const p3 = pts[(i + 2) % points];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return `${d} Z`;
}

/* Échelle cible d'une phase. Si `to` est défini, on l'utilise ;
   sinon : inspire monte, expire descend, rétention maintient l'état courant. */
function phaseTarget(phase, fromScale) {
  if (typeof phase.to === "number") return phase.to;
  if (phase.key === "inhale") return SCALE_MAX;
  if (phase.key === "exhale") return SCALE_MIN;
  return fromScale; // hold
}

/* === Tes 9 respirations (timings tirés de soundsDatabase) ===
   goal pilote la teinte : "calmer" / "dormir" / "focus".
   key : "inhale" (gonfle) | "exhale" (contracte) | "hold" (maintien).
   `to` (optionnel) force une échelle cible précise. dur = ms. */
const TECHNIQUES = {
  coherence: {
    label: "Cohérence cardiaque", goal: "calmer", name: "Cohérence cardiaque",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "tout en douceur", dur: 6000 },
      { key: "exhale", label: "Expirez", hint: "relâchez la pression", dur: 6000 },
    ],
  },
  carre: {
    label: "Carré", goal: "calmer", name: "Respiration carrée",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "par le nez", dur: 4000 },
      { key: "hold", label: "Retenez", hint: "poumons pleins", dur: 4000 },
      { key: "exhale", label: "Expirez", hint: "par la bouche", dur: 4000 },
      { key: "hold", label: "Retenez", hint: "poumons vides", dur: 4000, to: SCALE_MIN },
    ],
  },
  soupir: {
    label: "Soupir physiologique", goal: "calmer", name: "Soupir physiologique",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "grande inspiration", dur: 3000, to: 1.2 },
      { key: "hold", label: "Petite pause", hint: "un instant", dur: 800 },
      { key: "inhale", label: "Complétez", hint: "une dernière prise d'air", dur: 1500, to: SCALE_MAX },
      { key: "exhale", label: "Expirez longuement", hint: "laissez filer", dur: 8000 },
    ],
  },
  longueExpi: {
    label: "Longue expiration", goal: "dormir", name: "Longue expiration",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "par le nez", dur: 3000 },
      { key: "exhale", label: "Expirez", hint: "laissez filer", dur: 7000 },
    ],
  },
  relax478: {
    label: "4-7-8", goal: "dormir", name: "Respiration 4-7-8",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "par le nez", dur: 4000 },
      { key: "hold", label: "Retenez", hint: "en douceur", dur: 7000 },
      { key: "exhale", label: "Expirez", hint: "par la bouche", dur: 8000 },
    ],
  },
  alternee: {
    label: "Alternée", goal: "dormir", name: "Respiration alternée",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "narine gauche", dur: 3000 },
      { key: "exhale", label: "Expirez", hint: "narine droite", dur: 3000 },
    ],
  },
  triangle: {
    label: "Triangle", goal: "focus", name: "Respiration triangle",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "rassemblez l'énergie", dur: 4000 },
      { key: "hold", label: "Retenez", hint: "stabilisez", dur: 4000 },
      { key: "exhale", label: "Expirez longuement", hint: "coupez le bruit", dur: 8000 },
    ],
  },
  boosteur: {
    label: "Boosteur d'énergie", goal: "focus", name: "Boosteur d'énergie",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "vif", dur: 1500 },
      { key: "exhale", label: "Expirez", hint: "vif", dur: 1500 },
      { key: "hold", label: "Respiration normale", hint: "récupérez", dur: 6000 },
    ],
  },
  ventilation: {
    label: "Grande ventilation", goal: "focus", name: "Grande ventilation",
    phases: [
      { key: "inhale", label: "Inspirez", hint: "ventre, poitrine, clavicules", dur: 8000 },
      { key: "exhale", label: "Expirez", hint: "dans l'ordre inverse", dur: 8000 },
    ],
  },
};

export default function AuriaVisualizer() {
  const [fileName, setFileName] = useState("");
  const [playing, setPlaying] = useState(false);
  const [mood, setMood] = useState("coherence");
  const [ratio, setRatio] = useState("9x16");
  const [bg, setBg] = useState("/backgrounds/sommeil.jpg");
  const [phaseIndex, setPhaseIndex] = useState(0);

  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataRef = useRef(null);

  const stageRef = useRef(null);
  const rootRef = useRef(null);
  const membraneRef = useRef(null);
  const coreRef = useRef(null);
  const glowRef = useRef(null);
  const ringRef = useRef(null);

  const moodRef = useRef(mood);
  moodRef.current = mood;

  // Timing du cycle + état audio/échelle
  const bRef = useRef({ start: 0, index: 0, fromScale: 1 });
  const sRef = useRef({ level: 0, peak: 0.06, t: 0, last: 0, scale: 1 });

  // Reset propre quand on change de technique (repart de l'échelle courante)
  useEffect(() => {
    bRef.current = { start: performance.now(), index: 0, fromScale: sRef.current.scale || 1 };
    setPhaseIndex(0);
  }, [mood]);

  const ensureAudioGraph = useCallback(() => {
    if (ctxRef.current) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const source = ctx.createMediaElementSource(audioRef.current);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    analyser.connect(ctx.destination);
    ctxRef.current = ctx;
    analyserRef.current = analyser;
    dataRef.current = new Uint8Array(analyser.frequencyBinCount);
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    audioRef.current.src = URL.createObjectURL(file);
    setFileName(file.name);
    setPlaying(false);
  };

  const onPickTrack = (e) => {
    if (!e.target.value) return;
    audioRef.current.src = e.target.value;
    setFileName(e.target.value.split("/").pop());
    setPlaying(false);
  };

  const togglePlay = async () => {
    ensureAudioGraph();
    if (ctxRef.current.state === "suspended") await ctxRef.current.resume();
    if (audioRef.current.paused) {
      await audioRef.current.play();
      setPlaying(true);
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  // Boucle de rendu : le cycle de respiration pilote tout.
  useEffect(() => {
    let raf = 0;
    let mounted = true;

    const loop = (now) => {
      if (!mounted) return;
      const s = sRef.current;
      const b = bRef.current;
      const dt = s.last ? Math.min(0.05, (now - s.last) / 1000) : 0;
      s.last = now;
      s.t += dt * 0.6; // vie organique lente

      const tech = TECHNIQUES[moodRef.current] || TECHNIQUES.coherence;
      const phases = tech.phases;

      if (!b.start) b.start = now;

      // Avance dans le cycle (échelle continue : on mémorise l'échelle de départ)
      let phase = phases[b.index] || phases[0];
      let elapsed = now - b.start;
      if (elapsed >= phase.dur) {
        b.fromScale = s.scale;
        b.index = (b.index + 1) % phases.length;
        b.start = now;
        elapsed = 0;
        phase = phases[b.index];
        setPhaseIndex(b.index);
      }
      const p = clampNum(elapsed / phase.dur, 0, 1);

      const target = phaseTarget(phase, b.fromScale);
      const bScale = lerp(b.fromScale, target, smoothstep(p));
      s.scale = bScale;

      // Progression normalisée (0 = expir plein, 1 = inspir plein)
      const t01 = clampNum((bScale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN), 0, 1);

      // Dolly zoom : le fond contre-zoome par rapport à AURIA.
      const STR = 0.6; // 0 = aucun effet, 1 = vertige fort
      const depth = 1 + (lerp(1.4, 0.7, t01) - 1) * STR;
      const stage = stageRef.current;
      if (stage) stage.style.setProperty("--av-depth", depth.toFixed(3));

      // Audio → niveau global (colore la lueur uniquement)
      let level = 0;
      const analyser = analyserRef.current;
      const data = dataRef.current;
      if (analyser && data && !audioRef.current?.paused) {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) sum += data[i];
        level = sum / (data.length * 255);
      }
      s.peak = Math.max(level, s.peak * 0.999, 0.06);
      const nLevel = clampNum(level / s.peak, 0, 1);
      s.level += (nLevel - s.level) * 0.1;

      // Morph organique léger (jamais un cercle figé)
      const morph = Math.sin(s.t) * 1.6 + Math.sin(s.t * 0.37 + 1.3) * 1.0;
      const wobble = 1.5 + (Math.sin(s.t * 0.3) * 0.4 + 0.5) * 0.6;

      const membrane = membraneRef.current;
      if (membrane) {
        membrane.setAttribute("d", makeBlobPath({ t: s.t, radius: 70 * bScale + morph, wobble: wobble + 0.2 }));
      }
      const core = coreRef.current;
      if (core) {
        core.setAttribute("d", makeBlobPath({ t: s.t * 1.2, phase: 1.7, radius: 46 * bScale + morph * 0.6, wobble }));
      }
      const glow = glowRef.current;
      if (glow) {
        const gx = Math.sin(s.t * 0.23) * 8 + Math.sin(s.t * 0.11 + 1.5) * 5;
        const gy = Math.cos(s.t * 0.19 + 0.6) * 7 + Math.sin(s.t * 0.13 + 2.2) * 4;
        glow.setAttribute("transform", `translate(${gx.toFixed(2)},${gy.toFixed(2)})`);
      }
      const ring = ringRef.current;
      if (ring) {
        ring.style.strokeDashoffset = (RING_C * (1 - t01)).toFixed(2);
      }
      const root = rootRef.current;
      if (root) {
        const glowI = clampNum(0.32 + t01 * 0.5 + s.level * 0.3, 0, 1);
        root.style.setProperty("--auria-glow", glowI.toFixed(3));
      }

      raf = window.requestAnimationFrame(loop);
    };

    raf = window.requestAnimationFrame(loop);
    return () => {
      mounted = false;
      window.cancelAnimationFrame(raf);
    };
  }, []);

  const tech = TECHNIQUES[mood] || TECHNIQUES.coherence;
  const phase = tech.phases[phaseIndex] || tech.phases[0];

  return (
    <div className="av-page">
      <div
        ref={stageRef}
        className={`av-stage av-stage--${ratio}`}
        data-mood={tech.goal}
        style={{ "--av-bg": `url("${bg}")` }}
      >
        <div className="av-depth" aria-hidden="true">
          <div className="av-depth__img" />
          <div className="av-depth__veil" />
        </div>

        <span className="av-technique">{tech.name}</span>

        <div className="av-being" ref={rootRef}>
          <svg className="av-svg" viewBox="0 0 240 240" aria-hidden="true">
            <defs>
              <radialGradient id="av-membrane" cx="46%" cy="44%" r="68%">
                <stop offset="0%" stopColor="var(--av-m0)" />
                <stop offset="100%" stopColor="var(--av-m1)" />
              </radialGradient>
              <radialGradient id="av-core" cx="42%" cy="34%" r="76%">
                <stop offset="0%" stopColor="var(--av-c0)" />
                <stop offset="42%" stopColor="var(--av-c1)" />
                <stop offset="76%" stopColor="var(--av-c2)" />
                <stop offset="100%" stopColor="var(--av-c3)" />
              </radialGradient>
              <radialGradient id="av-iris" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.96)" />
                <stop offset="55%" stopColor="rgba(75,124,246,0.46)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
              <filter id="av-soft" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="8" />
              </filter>
              <filter id="av-mem" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
            </defs>

            <circle className="av-ring-track" cx="120" cy="120" r="100" />
            <circle ref={ringRef} className="av-ring" cx="120" cy="120" r="100" />

            <path
              ref={membraneRef}
              className="av-membrane"
              d={makeBlobPath({ t: 0, radius: 70, wobble: 1.7 })}
              fill="url(#av-membrane)"
              filter="url(#av-mem)"
            />
            <path
              ref={coreRef}
              className="av-core"
              d={makeBlobPath({ t: 1.7, phase: 1.7, radius: 46, wobble: 1.1 })}
              fill="url(#av-core)"
            />
            <g ref={glowRef} className="av-attn">
              <circle cx="120" cy="116" r="26" fill="url(#av-iris)" filter="url(#av-soft)" />
            </g>
          </svg>
        </div>

        <div className="av-caption" key={`${mood}-${phaseIndex}`}>
          <span className="av-caption__label">{phase.label}</span>
          <small className="av-caption__hint">{phase.hint}</small>
        </div>
      </div>

      <div className="av-controls">
        <div className="av-row">
          <label className="av-file">
            <input type="file" accept="audio/*" onChange={onFile} />
            <span>{fileName || "Charger un MP3"}</span>
          </label>
          <select className="av-file av-select" onChange={onPickTrack} defaultValue="">
            <option value="">Ou un morceau…</option>
            {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={`/sounds/audio/${n}.mp3`}>
                Morceau {n}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="av-play" onClick={togglePlay} disabled={!fileName}>
          {playing ? "Pause" : "Lecture"}
        </button>

        <select className="av-file av-select" value={mood} onChange={(e) => setMood(e.target.value)}>
          {Object.entries(TECHNIQUES).map(([key, val]) => (
            <option key={key} value={key}>
              {val.label}
            </option>
          ))}
        </select>

        <select className="av-file av-select" value={bg} onChange={(e) => setBg(e.target.value)}>
          <optgroup label="Ambiances">
            <option value="/backgrounds/sommeil.jpg">Sommeil</option>
            <option value="/backgrounds/stress.jpg">Anti-stress</option>
            <option value="/backgrounds/respi.jpg">Respiration</option>
          </optgroup>
          <optgroup label="Fonds du site">
            {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={`/backgrounds/${n}.jpg`}>
                Fond {n}
              </option>
            ))}
          </optgroup>
        </select>

        <div className="av-segmented">
          <button type="button" className={ratio === "9x16" ? "is-active" : ""} onClick={() => setRatio("9x16")}>
            Vertical
          </button>
          <button type="button" className={ratio === "16x9" ? "is-active" : ""} onClick={() => setRatio("16x9")}>
            Horizontal
          </button>
        </div>
      </div>

      <audio ref={audioRef} crossOrigin="anonymous" />
    </div>
  );
}